import React from 'react';
import { TrendingUp, TrendingDown, Zap, AlertTriangle } from 'lucide-react';

const fmtPrice = (v) => {
    if (v === null || v === undefined) return '—';
    if (v >= 1000) return v.toLocaleString('en-US', { maximumFractionDigits: 2 });
    if (v >= 1) return v.toLocaleString('en-US', { maximumFractionDigits: 4 });
    return v.toLocaleString('en-US', { maximumFractionDigits: 8 });
};

export default function HighProbabilityStrip({ rows, onSelect }) {
    const picks = (rows || []).filter((r) => r.high_probability);

    if (picks.length === 0) {
        return (
            <div
                data-testid="high-prob-strip-empty"
                className="border border-slate-700/80 bg-gradient-to-r from-slate-800 to-slate-900 rounded-md px-4 py-3 flex items-center gap-3"
            >
                <Zap className="h-4 w-4 text-slate-400" />
                <span className="text-[11px] text-slate-400 uppercase tracking-wider">
                    Yüksek İhtimal Şeridi (≥%80)
                </span>
                <span className="text-xs text-slate-400">
                    · Şu an %80+ güvenilirlikte fırsat yok — koşullar olgunlaşınca burada görünür.
                </span>
            </div>
        );
    }

    return (
        <div
            data-testid="high-prob-strip"
            className="border border-emerald-500/20 bg-gradient-to-r from-emerald-950/20 via-slate-900 to-slate-800 rounded-md overflow-hidden"
        >
            <div className="px-4 py-2 border-b border-emerald-500/10 flex items-center gap-2 flex-wrap">
                <Zap className="h-3.5 w-3.5 text-emerald-400" />
                <span className="text-[11px] uppercase tracking-[0.2em] text-emerald-300 font-medium">
                    Yüksek İhtimal Fırsatları · ŞİMDİ İşlem Açılabilir
                </span>
                <span className="text-[10px] text-slate-400">
                    güven ≥ %80 · R/R ≥ 1.5 · risk ≤ 65 → sinyal olgunlaşmış, giriş-hedef-stop belli
                </span>
                <span className="ml-auto text-[10px] text-slate-400">
                    {picks.length} coin
                </span>
            </div>
            <div className="flex gap-3 overflow-x-auto p-3 scrollbar-thin scrollbar-thumb-slate-600">
                {picks.map((r) => {
                    const isLong = r.recommendation?.action === 'LONG';
                    const Icon = isLong ? TrendingUp : TrendingDown;
                    const accent = isLong ? 'text-emerald-400 border-emerald-500/30 bg-emerald-500/5' : 'text-rose-400 border-rose-500/30 bg-rose-500/5';
                    const lev = r.recommendation?.leverage;
                    const levHigh = lev?.value >= 25;
                    const dirLabel = isLong ? 'AL (LONG)' : 'SAT (SHORT)';
                    return (
                        <button
                            key={r.symbol}
                            onClick={() => onSelect?.(r.symbol)}
                            data-testid={`high-prob-card-${r.symbol}`}
                            className={`group flex-shrink-0 w-[280px] text-left border ${accent} rounded-md p-3 hover:bg-slate-700/50 transition-colors`}
                        >
                            <div className="flex items-center justify-between mb-2">
                                <div className="flex items-center gap-1.5">
                                    <Icon className="h-3.5 w-3.5" />
                                    <span className="font-mono font-semibold text-slate-100 text-sm">
                                        {r.symbol.replace('USDT', '')}
                                    </span>
                                    <span className="text-[10px] text-slate-400">/USDT</span>
                                </div>
                                <span className={`text-[10px] font-mono font-bold px-1.5 py-0.5 rounded-sm ${isLong ? 'bg-emerald-500/15 text-emerald-200' : 'bg-rose-500/15 text-rose-200'}`}>
                                    güven %{r.recommendation?.confidence?.toFixed(0)}
                                </span>
                            </div>

                            {/* Big direction + profit label */}
                            <div className={`rounded-md mb-2 px-2 py-1.5 ${isLong ? 'bg-emerald-500/10' : 'bg-rose-500/10'}`}>
                                <div className="flex items-center justify-between">
                                    <div>
                                        <div className="text-[9px] uppercase tracking-wider text-slate-300">
                                            Yön · Olası kazanç
                                        </div>
                                        <div className={`text-sm font-bold ${isLong ? 'text-emerald-300' : 'text-rose-300'}`}>
                                            {dirLabel}
                                        </div>
                                    </div>
                                    <div className={`text-xl font-mono font-bold ${isLong ? 'text-emerald-300' : 'text-rose-300'}`}>
                                        +%{r.recommendation?.profit_pct?.toFixed(1) ?? '—'}
                                    </div>
                                </div>
                                <div className="text-[10px] text-slate-300 mt-0.5">
                                    Risk / Ödül: <span className="font-mono text-slate-100">1'e karşı {r.recommendation?.rr_ratio?.toFixed(2) ?? '—'}</span>
                                    {r.recommendation?.loss_pct && (
                                        <span className="text-rose-400 ml-2">· olası zarar %{r.recommendation.loss_pct}</span>
                                    )}
                                </div>
                            </div>

                            <div className="text-[11px] text-slate-200 mb-2 leading-snug min-h-[32px]">
                                {r.summary}
                            </div>

                            <div className="grid grid-cols-3 gap-1.5 text-[10px] font-mono mb-2">
                                <div>
                                    <div className="text-slate-400 uppercase tracking-wider text-[9px]">Giriş</div>
                                    <div className="text-slate-100">${fmtPrice(r.recommendation?.entry)}</div>
                                </div>
                                <div>
                                    <div className="text-slate-400 uppercase tracking-wider text-[9px]">Hedef (TP2)</div>
                                    <div className={isLong ? 'text-emerald-300' : 'text-rose-300'}>
                                        ${fmtPrice(r.recommendation?.tp2)}
                                    </div>
                                </div>
                                <div>
                                    <div className="text-slate-400 uppercase tracking-wider text-[9px]">Stop</div>
                                    <div className="text-amber-300">${fmtPrice(r.recommendation?.stop_loss)}</div>
                                </div>
                            </div>

                            <div className="flex items-center justify-between border-t border-slate-700/50 pt-1.5">
                                <div className="flex items-center gap-1.5">
                                    {levHigh && <AlertTriangle className="h-3 w-3 text-amber-400" />}
                                    <span className={`text-[10px] font-mono font-bold px-1.5 py-0.5 rounded-sm ${
                                        levHigh ? 'bg-amber-500/15 text-amber-300' : 'bg-slate-600 text-slate-100'
                                    }`}>
                                        {lev?.label || '—'}
                                    </span>
                                </div>
                                <span className="text-[9px] text-slate-400 uppercase tracking-wider">
                                    Tıkla → Pozisyon Hesapla
                                </span>
                            </div>
                        </button>
                    );
                })}
            </div>
        </div>
    );
}
